import React, { useState } from 'react';
import { Plus, Minus, DollarSign, StickyNote, RefreshCcw } from 'lucide-react';
import { CardCondition, CardVariant, CreateCardInstanceData } from '../types/collection.ts';
import { ConditionSelector, VariantSelector } from './ConditionBadge.tsx';
import Modal, { ModalBody } from './ui/Modal.tsx';
import Button from './ui/Button.tsx';
import Input from './ui/Input.tsx';

interface CardInfo {
  id: string;
  name: string;
  images?: { small?: string; large?: string };
  set?: { name?: string } | string;
  rarity?: string;
  cardmarket?: { prices?: { trendPrice?: number } };
}

interface AddCardModalProps {
  card: CardInfo;
  onClose: () => void;
  onAdd: (data: CreateCardInstanceData) => Promise<void>;
  existingQuantity?: number;
}

export default function AddCardModal({
  card,
  onClose,
  onAdd,
  existingQuantity = 0
}: AddCardModalProps) {
  const [condition, setCondition] = useState<CardCondition>('near_mint');
  const [variant, setVariant] = useState<CardVariant>('normal');
  const [quantity, setQuantity] = useState(1);
  const [acquiredPrice, setAcquiredPrice] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [forTrade, setForTrade] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setName = typeof card.set === 'string' ? card.set : card.set?.name || '';
  const marketPrice = card.cardmarket?.prices?.trendPrice;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await onAdd({
        cardId: card.id,
        condition,
        variant,
        quantity,
        acquiredPrice: acquiredPrice ? parseFloat(acquiredPrice) : undefined,
        notes: notes.trim() || undefined,
        forTrade
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card');
    } finally {
      setIsSubmitting(false);
    }
  };

  const incrementQuantity = () => setQuantity(q => Math.min(q + 1, 99));
  const decrementQuantity = () => setQuantity(q => Math.max(q - 1, 1));

  return (
    <Modal isOpen={true} onClose={onClose} title="Add to Collection" size="md">
      {/* Card Preview */}
      <div className="px-6 py-4 flex gap-4 border-b border-gray-800">
        <div className="w-24 h-32 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
          <img
            src={card.images?.small || card.images?.large || '/placeholder.svg'}
            alt={card.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white truncate">{card.name}</h3>
          <p className="text-sm text-gray-400 truncate">{setName}</p>
          {card.rarity && (
            <p className="text-sm text-gray-500">{card.rarity}</p>
          )}
          {marketPrice && (
            <p className="mt-2 text-sm">
              <span className="text-gray-400">Market Price: </span>
              <span className="text-green-400 font-medium">
                €{marketPrice.toFixed(2)}
              </span>
            </p>
          )}
          {existingQuantity > 0 && (
            <p className="mt-1 text-sm text-blue-400">
              You already own {existingQuantity} of this card
            </p>
          )}
        </div>
      </div>

      {/* Form */}
      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Condition
            </label>
            <ConditionSelector value={condition} onChange={setCondition} />
          </div>

          {/* Variant */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Variant
            </label>
            <VariantSelector value={variant} onChange={setVariant} />
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Quantity
            </label>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                className="w-10 h-10 p-0"
              >
                <Minus className="w-5 h-5" />
              </Button>
              <span className="text-2xl font-bold text-white w-12 text-center">
                {quantity}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={incrementQuantity}
                disabled={quantity >= 99}
                className="w-10 h-10 p-0"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Acquired Price (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Purchase Price (optional)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={acquiredPrice}
                onChange={(e) => setAcquiredPrice(e.target.value)}
                placeholder="0.00"
                className="pl-8"
              />
            </div>
            {marketPrice && !acquiredPrice && (
              <button
                type="button"
                onClick={() => setAcquiredPrice(marketPrice.toFixed(2))}
                className="mt-2 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
              >
                <RefreshCcw className="w-3 h-3" />
                Use market price (€{marketPrice.toFixed(2)})
              </button>
            )}
          </div>

          {/* Notes (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <StickyNote className="w-4 h-4 inline mr-1" />
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this card..."
              rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* For Trade Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-300">
                Available for Trade
              </label>
              <p className="text-xs text-gray-500">
                Show this card in the exchange marketplace
              </p>
            </div>
            <button
              type="button"
              onClick={() => setForTrade(!forTrade)}
              className={`
                relative w-12 h-6 rounded-full transition-colors
                ${forTrade ? 'bg-blue-600' : 'bg-gray-700'}
              `}
            >
              <span
                className={`
                  absolute top-1 w-4 h-4 rounded-full bg-white transition-transform
                  ${forTrade ? 'left-7' : 'left-1'}
                `}
              />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/30 border border-red-800 rounded-lg px-4 py-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={isSubmitting}
            leftIcon={Plus}
            className="w-full"
          >
            Add {quantity} Card{quantity > 1 ? 's' : ''} to Collection
          </Button>
        </form>
      </ModalBody>
    </Modal>
  );
}
